import './app/globals.css'
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { motion } from 'framer-motion';
import { 
  ChartBarIcon, 
  UserGroupIcon, 
  SignalIcon,
  BeakerIcon,
  CircleStackIcon,
  UserIcon,
  ScaleIcon,
  BoltIcon,
  MoonIcon,
  FaceSmileIcon,
  HomeIcon,
  ShieldCheckIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import ContactForm from '@/components/ContactForm';

function App() {
  return (
    <>
      <Navigation />
      
      {/* Hero Section with Magical Background */}
      <section className="relative pt-24 pb-16 sm:pt-32 sm:pb-24 lg:pb-32 overflow-hidden">
        {/* Magical Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-50 via-white to-secondary-50">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,0.1),transparent_50%)]"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(16,185,129,0.1),transparent_50%)]"></div>
        </div>
        
        {/* Floating Particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-primary-300 rounded-full opacity-60"
              animate={{
                y: [-20, -100],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
              style={{
                left: `${Math.random() * 100}%`,
                top: '100%',
              }}
            />
          ))}
        </div>

        <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-white/70 backdrop-blur-sm rounded-full border border-primary-200 shadow-lg"
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                <SparklesIcon className="h-5 w-5 text-primary-600" />
                <span className="text-sm font-medium text-primary-700">Powered by Advanced AI</span>
              </motion.div>
              
              <h1 className="text-4xl font-bold tracking-tight bg-gradient-to-r from-primary-900 via-primary-600 to-secondary-600 bg-clip-text text-transparent sm:text-6xl">
                Optimise Your Healthspan with AI
              </h1>
              <p className="mt-6 text-lg leading-8 text-gray-700">
                Using cutting-edge science and AI to help you live longer and better. We combine advanced technology with medical expertise to create personalised health optimisation plans, and work with you to help you reverse your biological age.
              </p>
              <div className="mt-10 flex items-center justify-center gap-x-6">
                <motion.a
                  href="#contact"
                  className="group relative rounded-full bg-gradient-to-r from-primary to-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-primary-600 to-secondary opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <span className="relative">Get Started</span>
                </motion.a>
                <motion.a 
                  href="#how-it-works" 
                  className="text-sm font-semibold leading-6 text-gray-900 hover:text-primary transition-colors group"
                  whileHover={{ x: 5 }}
                >
                  Learn more <span aria-hidden="true" className="group-hover:translate-x-1 transition-transform inline-block">→</span>
                </motion.a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 sm:py-32 bg-gradient-to-br from-gray-50 to-primary-50/30">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div 
            className="mx-auto max-w-2xl lg:text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-base font-semibold leading-7 text-primary">About Us</h2>
            <p className="mt-2 text-3xl font-bold tracking-tight bg-gradient-to-r from-primary-900 to-secondary-700 bg-clip-text text-transparent sm:text-4xl">
              Revolutionising Healthcare Through AI
            </p>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              We're combining the latest advances in medical science with artificial intelligence to create personalised health optimisation strategies that help you reverse your biological age.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className="py-24 sm:py-32 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-secondary-50/20 to-primary-50/30"></div>
        <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div 
            className="mx-auto max-w-2xl lg:text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-base font-semibold leading-7 text-primary">Our Team</h2>
            <p className="mt-2 text-3xl font-bold tracking-tight bg-gradient-to-r from-primary-900 to-secondary-700 bg-clip-text text-transparent sm:text-4xl">
              Expert Healthcare Leaders
            </p>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              Our founding team combines extensive expertise in medicine, technology, entrepreneurship, and healthcare innovation.
            </p>
          </motion.div>
          <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
            <div className="grid grid-cols-1 gap-x-8 gap-y-16 md:grid-cols-2">
              {[
                {
                  name: 'Fiona Phillips',
                  role: 'Co-founder and CEO',
                  image: '/images/team/fiona.jpg',
                  bio: 'Former financial derivatives trader from London, escaped the city to study for an MBA in Cape Town, subsequent multipreneur.',
                },
                {
                  name: 'Dr Nic Andela',
                  role: 'Co-founder and COO',
                  image: '/images/team/nic.jpg',
                  bio: 'MBChB, certified anaesthetist in SA and UK, passion for preventative health, pursuing health solutions that serve the masses.',
                },
                {
                  name: 'Dr Mark Anthony',
                  role: 'Co-founder and CMO',
                  image: '/images/team/mark.jpg',
                  bio: 'Intensive care doctor, Oxford, UK, holds multiple medical degrees, and is a member of the Healthy Longevity Medicine Society.',
                },
                {
                  name: 'Dr Adam Pantanowitz',
                  role: 'Technical Co-founder',
                  image: '/images/team/adam.jpg',
                  bio: 'Passionate about using technology to improve healthcare, Adam has obtained four degrees in engineering including his PhD. He has co-founded numerous companies and speaks across the globe on the future of health.',
                },
              ].map((member, index) => (
                <motion.div 
                  key={member.name} 
                  className="flex flex-col items-center text-center group"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                >
                  <div className="relative h-40 w-40 mb-6">
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-primary-200 to-secondary-200 opacity-20 group-hover:opacity-40 transition-opacity duration-300"></div>
                    <img
                      src={member.image}
                      alt={member.name}
                      className="relative h-full w-full rounded-full object-cover grayscale group-hover:grayscale-0 transition-all duration-300"
                    />
                    <div className="absolute inset-0 rounded-full bg-gradient-to-t from-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                  <h3 className="text-lg font-semibold leading-7 text-gray-900 group-hover:text-primary transition-colors">
                    {member.name}
                  </h3>
                  <p className="text-sm leading-6 text-primary font-medium">
                    {member.role}
                  </p>
                  <p className="mt-4 text-base leading-7 text-gray-600">
                    {member.bio}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How it Works Section */}
      <section id="how-it-works" className="py-24 sm:py-32 bg-gradient-to-br from-primary-50 via-white to-secondary-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div 
            className="mx-auto max-w-2xl lg:text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-base font-semibold leading-7 text-primary">Our Process</h2>
            <p className="mt-2 text-3xl font-bold tracking-tight bg-gradient-to-r from-primary-900 to-secondary-700 bg-clip-text text-transparent sm:text-4xl">
              How HealthAI Works
            </p>
          </motion.div>
          <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
            <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
              {[
                {
                  title: 'Data Collection',
                  description: 'We gather comprehensive health data through advanced diagnostics and monitoring.',
                  icon: BeakerIcon,
                  gradient: 'from-blue-500 to-cyan-500',
                },
                {
                  title: 'AI Analysis',
                  description: 'Our AI analyses your data to identify patterns and optimisation opportunities.',
                  icon: CircleStackIcon,
                  gradient: 'from-purple-500 to-pink-500',
                },
                {
                  title: 'Expert Guidance',
                  description: 'Medical professionals review AI insights and create personalised recommendations.',
                  icon: UserIcon,
                  gradient: 'from-emerald-500 to-teal-500',
                },
              ].map((feature, index) => (
                <motion.div 
                  key={feature.title} 
                  className="flex flex-col items-center text-center group"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                >
                  <div className={`mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r ${feature.gradient} shadow-lg group-hover:shadow-xl transition-all duration-300`}>
                    <feature.icon className="h-8 w-8 text-white" aria-hidden="true" />
                  </div>
                  <dt className="text-lg font-semibold leading-7 text-gray-900 group-hover:text-primary transition-colors">
                    {feature.title}
                  </dt>
                  <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                    <p className="flex-auto">{feature.description}</p>
                  </dd>
                </motion.div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      {/* Seven Pillars of Healthspan Section */}
      <section id="focus-areas" className="py-24 sm:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-primary-50/30 to-secondary-50/30"></div>
        <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div 
            className="mx-auto max-w-2xl lg:text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-base font-semibold leading-7 text-primary">Our Focus</h2>
            <p className="mt-2 text-3xl font-bold tracking-tight bg-gradient-to-r from-primary-900 to-secondary-700 bg-clip-text text-transparent sm:text-4xl">
              Comprehensive Health Optimisation
            </p>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              Our AI-driven insights and expert guidance cover seven key pillars to enhance your healthspan and well-being.
            </p>
          </motion.div>
          <div className="mt-16 flex flex-col items-center sm:mt-20 lg:mt-24 space-y-16">
            {(() => {
              const pillars = [
                {
                  name: 'Movement',
                  description: 'Personalised exercise and movement routines to enhance your physical performance and longevity.',
                  icon: BoltIcon,
                  color: 'text-orange-600',
                  bgColor: 'bg-gradient-to-br from-orange-50 to-orange-100',
                  features: [
                    'Strength training protocols',
                    'Cardiovascular optimisation',
                    'Mobility and flexibility',
                    'Recovery strategies'
                  ]
                },
                {
                  name: 'Sleep',
                  description: 'Optimise your sleep quality and patterns for enhanced recovery and cognitive performance.',
                  icon: MoonIcon,
                  color: 'text-purple-600',
                  bgColor: 'bg-gradient-to-br from-purple-50 to-purple-100',
                  features: [
                    'Sleep environment optimisation',
                    'Circadian rhythm regulation',
                    'Sleep quality tracking',
                    'Recovery enhancement'
                  ]
                },
                {
                  name: 'Nutrition',
                  description: 'Personalised nutrition strategies based on your unique metabolic profile and health goals.',
                  icon: ScaleIcon,
                  color: 'text-green-600',
                  bgColor: 'bg-gradient-to-br from-green-50 to-green-100',
                  features: [
                    'Personalised meal plans',
                    'Nutrient optimisation',
                    'Metabolic health tracking',
                    'Supplement protocols'
                  ]
                },
                {
                  name: 'Stress Management',
                  description: 'Evidence-based techniques to manage stress and build resilience for optimal mental health.',
                  icon: FaceSmileIcon,
                  color: 'text-blue-600',
                  bgColor: 'bg-gradient-to-br from-blue-50 to-blue-100',
                  features: [
                    'Mindfulness practices',
                    'Stress biomarker tracking',
                    'Resilience building',
                    'Mental health support'
                  ]
                },
                {
                  name: 'Environment',
                  description: 'Optimise your living and working environments to support your health and wellbeing.',
                  icon: HomeIcon,
                  color: 'text-teal-600',
                  bgColor: 'bg-gradient-to-br from-teal-50 to-teal-100',
                  features: [
                    'Air quality monitoring',
                    'Light exposure optimisation',
                    'Toxin reduction strategies',
                    'Biohacking techniques'
                  ]
                },
                {
                  name: 'Social Connection',
                  description: 'Build meaningful relationships and social connections that support your mental and physical health.',
                  icon: UserGroupIcon,
                  color: 'text-pink-600',
                  bgColor: 'bg-gradient-to-br from-pink-50 to-pink-100',
                  features: [
                    'Community building',
                    'Relationship quality assessment',
                    'Social support networks',
                    'Communication skills'
                  ]
                },
                {
                  name: 'Medical Optimisation',
                  description: 'Advanced medical assessments and interventions to optimise your biological markers.',
                  icon: ShieldCheckIcon,
                  color: 'text-red-600',
                  bgColor: 'bg-gradient-to-br from-red-50 to-red-100',
                  features: [
                    'Comprehensive health assessments',
                    'Biomarker optimisation',
                    'Preventive care protocols',
                    'Longevity interventions'
                  ]
                }
              ];

              return pillars.map((pillar, index) => (
                <motion.div
                  key={pillar.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.02, y: -5 }}
                  className={`w-full max-w-4xl ${pillar.bgColor} rounded-3xl p-8 md:p-12 shadow-lg hover:shadow-xl transition-all duration-300 backdrop-blur-sm border border-white/20`}
                >
                  <div className="flex flex-col md:flex-row items-center gap-8">
                    <div className="flex-shrink-0">
                      <div className={`flex h-24 w-24 items-center justify-center rounded-full bg-white shadow-lg hover:shadow-xl transition-shadow duration-300`}>
                        <pillar.icon className={`h-12 w-12 ${pillar.color}`} aria-hidden="true" />
                      </div>
                    </div>
                    <div className="flex-grow text-center md:text-left">
                      <h3 className={`text-2xl font-bold ${pillar.color} mb-4`}>
                        {pillar.name}
                      </h3>
                      <p className="text-lg text-gray-700 mb-6">
                        {pillar.description}
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {pillar.features.map((feature) => (
                          <div key={feature} className="flex items-center gap-2">
                            <div className={`h-2 w-2 rounded-full ${pillar.color.replace('text-', 'bg-')}`}></div>
                            <span className="text-sm text-gray-600">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ));
            })()}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 sm:py-32 bg-gradient-to-br from-primary-50 via-white to-secondary-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div 
            className="mx-auto max-w-2xl lg:text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-base font-semibold leading-7 text-primary">Features</h2>
            <p className="mt-2 text-3xl font-bold tracking-tight bg-gradient-to-r from-primary-900 to-secondary-700 bg-clip-text text-transparent sm:text-4xl">
              Advanced Health Technology
            </p>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              Our platform combines cutting-edge technology with medical expertise to deliver personalised health insights.
            </p>
          </motion.div>
          <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
            <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
              {[
                {
                  name: 'AI-Powered Analytics',
                  description: 'Our advanced AI algorithms analyse thousands of health data points to provide personalised insights.',
                  icon: ChartBarIcon,
                  gradient: 'from-indigo-500 to-purple-500',
                },
                {
                  name: 'Expert Medical Team',
                  description: 'Board-certified physicians and health experts review all recommendations and provide ongoing support.',
                  icon: UserGroupIcon,
                  gradient: 'from-emerald-500 to-cyan-500',
                },
                {
                  name: 'Real-time Monitoring',
                  description: 'Continuous tracking of your health metrics with real-time feedback and adjustments.',
                  icon: SignalIcon,
                  gradient: 'from-orange-500 to-red-500',
                },
              ].map((feature, index) => (
                <motion.div 
                  key={feature.name} 
                  className="flex flex-col group"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                >
                  <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900 group-hover:text-primary transition-colors">
                    <div className={`flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-r ${feature.gradient} shadow-lg`}>
                      <feature.icon className="h-5 w-5 text-white" aria-hidden="true" />
                    </div>
                    {feature.name}
                  </dt>
                  <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                    <p className="flex-auto">{feature.description}</p>
                  </dd>
                </motion.div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="relative py-24 sm:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900 via-primary-800 to-secondary-700"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.1),transparent_70%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(16,185,129,0.2),transparent_70%)]"></div>
        
        <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div 
            className="mx-auto max-w-2xl lg:text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-base font-semibold leading-7 text-secondary-300">Get Started</h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Ready to Optimise Your Health?
            </p>
            <p className="mt-6 text-lg leading-8 text-gray-300">
              Take the first step towards reversing your biological age and optimising your healthspan.
            </p>
          </motion.div>
          <motion.div 
            className="mx-auto mt-16 max-w-xl sm:mt-20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl border border-white/20">
              <ContactForm />
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
}

export default App;
